import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { CreditCard, Eye, Package, RefreshCcw, Search as SearchIcon, Truck } from "lucide-react";

import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";

import sellersApi from "@/services/api/sellers";
import type { Order } from "@/services/types/domain";
import { formatPrice, tOrderStatus, tPaymentStatus, orderVariant, paymentVariant } from "@/utils/formatters";

// --------------------------------------------------
// Tipos locales (lo que este componente espera del servicio)
// --------------------------------------------------
export type SellerOrderRow = Order & {
    seller_revenue_in_order: number;   // ingreso del vendedor (PEN)
    seller_items_in_order: number;     // cantidad de items del vendedor en la orden
    last_name?: string;
    first_name?: string;
    email?: string;
};

export interface SellerOrdersResult {
    items: SellerOrderRow[];
    meta: {
        current_page: number;
        per_page: number;
        total: number;
        last_page: number;
    };
}

// --------------------------------------------------
// Constantes y utilidades
// --------------------------------------------------
const DEFAULT_PER_PAGE = 20;
const DEBOUNCE_MS = 500; // evita saturar el backend mientras se escribe

function useDebouncedValue<T>(value: T, delay = DEBOUNCE_MS) {
    const [debounced, setDebounced] = React.useState(value);
    useEffect(() => {
        const id = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(id);
    }, [value, delay]);
    return debounced;
}

const statusOptions = [
    { value: "", label: "Todos los estados" },
    { value: "pending", label: "Pendiente" },
    { value: "processing", label: "En proceso" },
    { value: "shipped", label: "Enviado" },
    { value: "delivered", label: "Entregado" },
    { value: "cancelled", label: "Cancelado" },
    { value: "returned", label: "Devuelto" },
];

const payStatusOptions = [
    { value: "", label: "Todos los pagos" },
    { value: "pending", label: "Pendiente" },
    { value: "paid", label: "Pagado" },       // UI (si tu API envía "completed", lo mostramos como "paid")
    { value: "refunded", label: "Reembolsado" },
    { value: "failed", label: "Fallido" },
];

const sortOptions = [
    { value: "created_desc", label: "Más recientes" },
    { value: "created_asc", label: "Más antiguos" },
    { value: "total_desc", label: "Total más alto" },
    { value: "total_asc", label: "Total más bajo" },
];

const isPaymentCompleted = (v?: string) => v === "paid" || v === "completed";

// --------------------------------------------------
// Componente principal
// --------------------------------------------------
const SellerOrdersPage: React.FC = () => {
    const [params, setParams] = useSearchParams();

    // Estados
    const [rows, setRows] = useState<SellerOrderRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [meta, setMeta] = useState<SellerOrdersResult["meta"]>({
        current_page: Number(params.get("page") || 1),
        per_page: Number(params.get("per_page") || DEFAULT_PER_PAGE),
        total: 0,
        last_page: 1,
    });

    // Filtros (URL)
    const page = Number(params.get("page") || 1);
    const per_page = Number(params.get("per_page") || DEFAULT_PER_PAGE);
    const q = params.get("q") || ""; // lo mapeamos a `search` en el servicio
    const status = params.get("status") || "";
    const payment_status = params.get("payment_status") || "";
    const sort = params.get("sort") || "created_desc";
    const date_from = params.get("date_from") || "";
    const date_to = params.get("date_to") || "";

    // Borrador del input + debounce
    const [searchDraft, setSearchDraft] = useState<string>(q);
    const debouncedQ = useDebouncedValue(searchDraft);
    useEffect(() => { setSearchDraft(q); }, [q]); // sincroniza con URL cambios externos

    // Evitar condiciones de carrera
    const latestReq = useRef(0);

    // busy por fila para acción de "enviar"
    const shipBusy = useRef<Set<number>>(new Set());
    const isShipBusy = (id: number) => shipBusy.current.has(id);
    const setShipBusy = (id: number, busy: boolean) => {
        const s = shipBusy.current;
        busy ? s.add(id) : s.delete(id);
        setRows(r => [...r]); // fuerza render
    };

    // Actualizar URL con filtros
    const submitFilters = (next: Partial<Record<string, string | number>>) => {
        const merged = new URLSearchParams(params);
        Object.entries(next).forEach(([k, v]) => {
            if (v === "" || v === undefined || v === null) merged.delete(k);
            else merged.set(k, String(v));
        });
        if (!("page" in next)) merged.set("page", "1");
        setParams(merged, { replace: true });
    };

    // Solo buscar cuando el usuario deja de escribir
    useEffect(() => {
        if (debouncedQ !== q) submitFilters({ q: debouncedQ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedQ]);

    // Carga de datos
    const fetchData = async () => {
        const reqId = ++latestReq.current;
        try {
            setLoading(true);

            const result: SellerOrdersResult = await (sellersApi as any).getSellerOrders({
                page,
                per_page,
                q, // el servicio lo mapea a ?search=
                status: status || undefined,
                payment_status: payment_status || undefined,
                sort,
                date_from: date_from || undefined,
                date_to: date_to || undefined,
            });

            if (reqId !== latestReq.current) return; // ignora respuestas antiguas
            setRows(result.items);
            setMeta(result.meta);
        } catch (err: any) {
            if (reqId !== latestReq.current) return;
            const msg = err?.response?.data?.message || err?.message || "No se pudieron cargar los pedidos";
            toast.error(msg);
        } finally {
            if (reqId === latestReq.current) setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, per_page, q, status, payment_status, sort, date_from, date_to]);

    const goPage = (p: number) => {
        if (p < 1 || p > meta.last_page) return;
        submitFilters({ page: p });
    };

    const resetFilters = () => {
        setParams(new URLSearchParams({ page: "1", per_page: String(DEFAULT_PER_PAGE), sort: "created_desc" }), { replace: true });
    };

    // ----------------------------
    // Acción: marcar como enviado
    // ----------------------------
    const handleMarkShipped = async (orderId: number) => {
        const row = rows.find(r => r.id === orderId);
        if (!row) return;

        // Validación: pago debe estar completo y estado en processing
        const paidOk = isPaymentCompleted(row.payment_status);
        const canShip = paidOk && row.status === "processing";
        if (!canShip) {
            toast.error("Para marcar como enviado: el pago debe estar completado y el pedido en proceso.");
            return;
        }

        /*if (typeof (sellersApi as any).markSellerOrderShipped !== "function") {
            toast.error("Acción no disponible. Falta implementar sellersApi.markSellerOrderShipped");
            return;
        }*/
        if (typeof (sellersApi as any).updateOrderStatus !== "function") {
            toast.error("Acción no disponible. Falta implementar sellersApi.updateOrderStatus");
            return;
        }

        setShipBusy(orderId, true);
        try {
            await (sellersApi as any).updateOrderStatus(orderId, "shipped", "Pedido enviado por el vendedor");
            // Actualizamos estado local
            setRows(prev => prev.map(o => o.id === orderId ? { ...o, status: "shipped", updated_at: new Date().toISOString() } : o));
            toast.success("Pedido marcado como Enviado");
        } catch (err: any) {
            toast.error(err?.response?.data?.message || err?.message || "No se pudo marcar como enviado");
        } finally {
            setShipBusy(orderId, false);
        }
    };

    const empty = !loading && rows.length === 0;

    return (
        <div className="min-h-screen bg-gray-50 px-4 py-6">
            <div className="mx-auto w-full max-w-7xl">
                {/* Header */}
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Pedidos de la tienda</h1>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={fetchData} leftIcon={<RefreshCcw className="w-4 h-4" />}>Actualizar</Button>
                    </div>
                </div>

                {/* Filtros */}
                <div className="mb-4 grid grid-cols-1 gap-3 rounded-2xl border bg-white p-3 sm:p-4 sm:grid-cols-12">
                    <div className="sm:col-span-4">
                        <Input
                            label="Buscar"
                            placeholder="N° de pedido, email, nombre… (Enter para buscar)"
                            value={searchDraft}
                            onChange={(e) => setSearchDraft(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") submitFilters({ q: searchDraft }); }}
                            leftIcon={<SearchIcon className="w-4 h-4" />}
                            fullWidth
                        />
                    </div>
                    <div className="sm:col-span-2">
                        <label className="mb-1 block text-sm font-medium text-gray-700">Estado</label>
                        <select
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={status}
                            onChange={(e) => submitFilters({ status: e.target.value })}
                        >
                            {statusOptions.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="sm:col-span-2">
                        <label className="mb-1 block text-sm font-medium text-gray-700">Pago</label>
                        <select
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={payment_status}
                            onChange={(e) => submitFilters({ payment_status: e.target.value })}
                        >
                            {payStatusOptions.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="sm:col-span-2">
                        <label className="mb-1 block text-sm font-medium text-gray-700">Ordenar</label>
                        <select
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={sort}
                            onChange={(e) => submitFilters({ sort: e.target.value })}
                        >
                            {sortOptions.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="sm:col-span-2 flex items-end gap-2">
                        <Button variant="outline" onClick={() => submitFilters({ q: searchDraft })}>Buscar</Button>
                        <Button variant="outline" onClick={resetFilters}>Limpiar</Button>
                    </div>
                </div>

                {/* Tabla / Lista */}
                <div className="rounded-2xl border bg-white">
                    <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 text-xs font-semibold text-gray-500">
                        <div className="col-span-1">Fecha</div>
                        <div className="col-span-2">Pedido</div>
                        <div className="col-span-2">Cliente</div>
                        <div className="col-span-2 text-right">Ingresos (tienda)</div>
                        <div className="col-span-1 text-right">Ítems</div>
                        <div className="col-span-1">Estados</div>
                        <div className="col-span-3 text-right">Acciones</div>
                    </div>
                    <div className="divide-y">
                        {loading && (
                            Array.from({ length: 6 }).map((_, i) => (
                                <div key={i} className="px-4 py-4 animate-pulse">
                                    <div className="h-6 bg-gray-100 rounded w-2/3" />
                                </div>
                            ))
                        )}

                        {!loading && empty && (
                            <div className="px-4 py-8 text-center text-gray-600">No hay pedidos para mostrar.</div>
                        )}

                        {!loading && rows.map((o) => {
                            const paymentUi = (o.payment_status as any);
                            const canShip = isPaymentCompleted(o.payment_status as any) && o.status === "processing";
                            return (
                                <div key={o.id} className="px-4 py-3 grid grid-cols-1 gap-2 md:grid-cols-12 md:items-center">
                                    <div className="md:col-span-1 text-sm text-gray-700">{o.created_at ? new Date(o.created_at).toLocaleString() : "—"}</div>
                                    <div className="md:col-span-2">
                                        <div className="text-sm font-semibold text-gray-900">{o.order_number || `#${o.id}`}</div>
                                        <div className="text-xs text-gray-500">{o.id}</div>
                                    </div>
                                    <div className="md:col-span-2 text-sm text-gray-700">
                                        {o?.first_name || o?.last_name ? (
                                            <span>{[o?.first_name, o?.last_name].filter(Boolean).join(" ")}</span>
                                        ) : (
                                            <span>{(o as any).email || "—"}</span>
                                        )}
                                    </div>
                                    <div className="md:col-span-2 text-right text-sm font-semibold text-gray-900">
                                        {formatPrice(Number(o.seller_revenue_in_order ?? 0), o.currency || "PEN")}
                                        <div className="text-xs text-gray-500">Total pedido: {formatPrice(Number(o.total_amount ?? 0), o.currency || "PEN")}</div>
                                    </div>
                                    <div className="md:col-span-1 text-right text-sm text-gray-700">{o.seller_items_in_order ?? 0}</div>

                                    {/* Estados */}
                                    <div className="md:col-span-2 flex flex-col gap-1">
                                        <div className="flex items-center gap-1">
                                            <span className="text-[11px] text-gray-500">Pedido</span>
                                            <Badge variant={orderVariant(o.status)} icon={<Package className="w-3.5 h-3.5" />}>
                                                {tOrderStatus(o.status)}
                                            </Badge>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <span className="text-[11px] text-gray-500">Pago</span>
                                            <Badge variant={paymentVariant(paymentUi)} icon={<CreditCard className="w-3.5 h-3.5" />}>
                                                {tPaymentStatus(paymentUi)}
                                            </Badge>
                                        </div>
                                    </div>

                                    {/* Acciones */}
                                    <div className="md:col-span-2 flex gap-2 md:justify-end">
                                        <Button asChild size="sm" variant="outline" title="Ver detalle">
                                            <Link to={`/seller/orders/${o.id}`}>
                                                <Eye className="w-4 h-4" />
                                            </Link>
                                        </Button>

                                        {/* Marcar como enviado */}
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleMarkShipped(o.id)}
                                            disabled={!canShip || isShipBusy(o.id)}
                                            title={
                                                canShip
                                                    ? "Marcar como Enviado"
                                                    : "Requiere pago completado y estado 'En proceso'"
                                            }
                                            leftIcon={<Truck className="w-4 h-4" />}
                                        >
                                            Enviar
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Paginación */}
                <div className="mt-6 flex items-center justify-center gap-3">
                    <Button variant="outline" size="sm" onClick={() => goPage(meta.current_page - 1)} disabled={meta.current_page <= 1}>
                        Anterior
                    </Button>
                    <span className="text-sm text-gray-700">
                        Página <strong>{meta.current_page}</strong> de <strong>{meta.last_page}</strong>
                    </span>
                    <Button variant="outline" size="sm" onClick={() => goPage(meta.current_page + 1)} disabled={meta.current_page >= meta.last_page}>
                        Siguiente
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default SellerOrdersPage;
